/**
 * Browsers (and embedded previews) render the full href in the
 * "open this link?" confirm. A production brief in `body=` makes that
 * overlay taller than the viewport, so Close is unreachable.
 */
export const VENDOR_EMAIL_MAILTO_HREF_MAX = 800;

const BODY_EXCERPT_MAX = 240;

export function buildVendorEmailMailtoHref(input: {
  to: readonly string[];
  cc?: readonly string[];
  subject: string;
  body: string;
  truncatedNote: string;
}): string {
  const to = input.to.join(",");
  const cc = (input.cc ?? []).join(",");
  const subject = input.subject.replace(/[\r\n]+/g, " ");

  const hrefFor = (body: string | null, includeSubject = true): string => {
    const params = new URLSearchParams();
    if (cc) params.set("cc", cc);
    if (includeSubject && subject) params.set("subject", subject);
    if (body) params.set("body", body);
    const query = params.toString();
    return query.length > 0 ? `mailto:${to}?${query}` : `mailto:${to}`;
  };

  const full = hrefFor(input.body);
  if (full.length <= VENDOR_EMAIL_MAILTO_HREF_MAX) return full;

  let body = input.body.trim();
  const note = input.truncatedNote.trim();
  if (body.length > BODY_EXCERPT_MAX) {
    body = `${body.slice(0, BODY_EXCERPT_MAX).trimEnd()}…`;
  }
  if (note) body = body.length > 0 ? `${body}\n\n${note}` : note;

  let href = hrefFor(body);
  while (href.length > VENDOR_EMAIL_MAILTO_HREF_MAX && body.length > 0) {
    body = body.slice(0, Math.max(0, body.length - 32)).trimEnd();
    href = hrefFor(body);
  }
  if (href.length <= VENDOR_EMAIL_MAILTO_HREF_MAX) return href;

  href = hrefFor(null);
  if (href.length <= VENDOR_EMAIL_MAILTO_HREF_MAX) return href;

  href = hrefFor(null, false);
  if (href.length <= VENDOR_EMAIL_MAILTO_HREF_MAX) return href;

  return `mailto:${to}`.slice(0, VENDOR_EMAIL_MAILTO_HREF_MAX);
}
